import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@src/api/config";
import { showModalSuccess } from "../../../../redux/modalTypeSuccess";
import { AppDispatch, RootState } from "@src/redux/store";

const urlBase = "api/insured_amounts";

type RequestParams = Record<string, any>;

const getOptionsArray = (payload: any) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

export interface SumInsuredValuePayload {
  cantidad: string;
  fee: number | string;
  from?: string;
  provider_id?: number | string;
  [key: string]: any;
}

export interface SumInsuredValueState {
  data: any;
  suggestions: any[];
  defaultValues: SumInsuredValuePayload | null;
  currentPage: number;
  params: RequestParams;
  meta: any;
  total: number;
}

export const getSumInsuredValues = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("insuredSumValues/getAll", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get(urlBase, { params: query });
  return {
    params: query,
    data: response.data
  };
});

export const getSumInsuredValueSuggestions = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("insuredSumValues/getSuggestions", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get(`${urlBase}/suggestions`, { params: query });
  return {
    data: response.data
  };
});

export const getSumInsuredValueById = createAsyncThunk<any, number>(
  "insuredSumValues/getById",
  async (id: number) => {
    const response = await api.get(`${urlBase}/${id}`);
    return {
      data: response.data
    };
  }
);

export const createSumInsuredValue = createAsyncThunk<
  any,
  SumInsuredValuePayload,
  { dispatch: AppDispatch; state: RootState }
>("insuredSumValues/create", async (payload, { dispatch, getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const body = {
    provider_id: payload.provider_id,
    business_line_id: businessLineSelected?.id,
    product_id: productSelected?.value,
    amount: payload.cantidad,
    fee: payload.fee
  };

  const response = await api.post(urlBase, body);

  if (response.data.status === "success") {
    if (payload.from === "providers") {
      dispatch(getSumInsuredValues({ format: "options", free: true }));
    }
    await dispatch(
      showModalSuccess(
        payload.from === "providers"
          ? { message: response.data.message }
          : { message: response.data.message, url: "goBack" }
      )
    );
  }

  return {
    data: response.data
  };
});

export const updateSumInsuredValue = createAsyncThunk<
  any,
  { id: number | string; data: SumInsuredValuePayload },
  { dispatch: AppDispatch }
>("insuredSumValues/update", async ({ id, data }, { dispatch }) => {
  const payload = {
    provider_id: data.provider_id,
    business_line_id: data.business_line_id,
    product_id: data.product_id,
    amount: data.cantidad,
    fee: data.fee
  };
  const response = await api.put(`${urlBase}/${id}`, payload);

  if (response.data.status === "success") {
    const url = "goBack";
    dispatch(showModalSuccess({ message: response.data.message, url }));
  }

  return {
    data: response.data
  };
});

export const deleteSumInsuredValue = createAsyncThunk<
  any,
  number,
  { dispatch: AppDispatch }
>("insuredSumValues/delete", async (id, { dispatch }) => {
  const response = await api.delete(`${urlBase}/${id}`);

  if (response.data.status === "success") {
    dispatch(showModalSuccess({ message: response.data.message }));
    await dispatch(getSumInsuredValues());
  }

  return {
    data: response.data
  };
});

const initialState: SumInsuredValueState = {
  data: [],
  suggestions: [],
  defaultValues: null,
  currentPage: 1,
  params: {},
  meta: {},
  total: 0
};

export const sumInsuredValuesSlice = createSlice({
  name: "insuredSumValues",
  initialState,
  reducers: {
    resetSumInsuredValues: (state) => {
      state.data = [];
      state.suggestions = [];
      state.defaultValues = null;
      state.currentPage = 1;
      state.params = {};
      state.meta = {};
      state.total = 0;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getSumInsuredValues.fulfilled, (state, action) => {
      if (action.payload?.params?.format === "options") {
        state.suggestions = getOptionsArray(action.payload.data);
      } else {
        state.data = action.payload.data;
        state.currentPage = action.payload.data?.current_page;
        state.params = action.payload.params;
        state.meta = action.payload.data?.meta;
        state.total = action.payload.data?.meta?.total;
      }
    });
    builder.addCase(getSumInsuredValueById.fulfilled, (state, action) => {
      const payloadData = action.payload?.data?.data || {};
      state.defaultValues = {
        cantidad: payloadData.amount ?? "",
        fee: payloadData.fee ?? "",
        provider_id: payloadData.provider_id ?? payloadData.provider?.id ?? ""
      };
    });
    builder.addCase(
      getSumInsuredValueSuggestions.fulfilled,
      (state, action) => {
        state.suggestions = getOptionsArray(action.payload.data);
      }
    );
  }
});

export const { resetSumInsuredValues } = sumInsuredValuesSlice.actions;

export default sumInsuredValuesSlice.reducer;
